<!--
  Angular 15 here, so this is *ngIf / *ngFor throughout - the @if block syntax used in the web app
  does not exist before Angular 17.
-->
<div class="dl-page">

  <div class="dl-page__header">
    <div class="dl-page__heading">
      <h1 class="dl-page__title">Shipment SMS</h1>
      <p class="dl-page__subtitle">Every text a courier sent about a bus, and how far it got.</p>
    </div>
  </div>

  <!-- The funnel, in the order it happens. Each tile is a smaller number than the one before it,
       and where the drop is tells you what to fix: delivery, the page, or the offer. -->
  <section class="dl-stat-grid" *ngIf="summary as s">
    <div class="dl-stat">
      <span class="dl-stat__value">{{ s.sent }}</span>
      <span class="dl-stat__label">Sent</span>
      <span class="dl-stat__sub">{{ s.segmentsSent }} billed segments</span>
    </div>

    <div class="dl-stat">
      <span class="dl-stat__value">{{ s.opened }}</span>
      <span class="dl-stat__label">Links opened</span>
      <span class="dl-stat__sub">
        <ng-container *ngIf="openRate !== null; else noLinks">
          {{ openRate }}% of {{ s.linksIssued }} links
        </ng-container>
        <ng-template #noLinks>no links issued yet</ng-template>
      </span>
    </div>

    <div class="dl-stat dl-stat--good">
      <span class="dl-stat__value">{{ s.joinRequested }}</span>
      <span class="dl-stat__label">Asked to join</span>
      <!-- Of those who opened, not of everything sent: somebody who never opened it was never
           asked, and counting them would hide a delivery problem as a conversion problem. -->
      <span class="dl-stat__sub">
        <ng-container *ngIf="joinRate !== null; else noOpens">{{ joinRate }}% of opened</ng-container>
        <ng-template #noOpens>nobody has opened one yet</ng-template>
      </span>
    </div>

    <!-- Not a funnel number. It sits with them because this is where somebody is already looking,
         and because it is the only figure here that asks for a decision rather than a trend. -->
    <div class="dl-stat" [class.dl-stat--bad]="s.emailOverridden > 0">
      <span class="dl-stat__value">{{ s.emailOverridden }}</span>
      <span class="dl-stat__label">Changed the address</span>
      <span class="dl-stat__sub">
        <ng-container *ngIf="s.emailOverridden > 0; else nobodyChanged">
          check who is holding those links
        </ng-container>
        <ng-template #nobodyChanged>everyone confirmed what we had</ng-template>
      </span>
    </div>

    <div class="dl-stat" [class.dl-stat--bad]="s.failed > 0">
      <span class="dl-stat__value">{{ s.failed }}</span>
      <span class="dl-stat__label">Failed</span>
      <span class="dl-stat__sub">
        <ng-container *ngIf="s.pending > 0; else fixThem">{{ s.pending }} still sending</ng-container>
        <ng-template #fixThem>each one is a number to correct</ng-template>
      </span>
    </div>
  </section>

  <section class="dl-card sms-filters">
    <select [(ngModel)]="statusFilter" (ngModelChange)="onFilterChange()" class="dl-select sms-status-select">
      <option *ngFor="let option of statusOptions" [ngValue]="option.value">{{ option.label }}</option>
    </select>

    <label class="dl-switch">
      <input type="checkbox" [(ngModel)]="joinOnly" (ngModelChange)="onFilterChange()"/>
      <span class="dl-switch__track"></span>
      <span class="dl-switch__label">Only the ones who asked to join</span>
    </label>

    <label class="dl-switch">
      <input type="checkbox" [(ngModel)]="overriddenOnly" (ngModelChange)="onFilterChange()"/>
      <span class="dl-switch__track"></span>
      <span class="dl-switch__label">Only the ones who changed the address</span>
    </label>
  </section>

  <div class="dl-banner dl-banner--error" *ngIf="error">
    <mat-icon>error</mat-icon>
    <div class="dl-banner__body">{{ error }}</div>
  </div>

  <div class="dl-loading" *ngIf="loading">
    <div class="dl-spinner"></div>
    <p>Loading…</p>
  </div>

  <app-empty-state icon="sms" title="Nothing to show" *ngIf="!loading && rows.length === 0">
    <ng-container *ngIf="overriddenOnly; else maybeJoinOnly">
      Nobody has replaced the address we hold.
    </ng-container>
    <ng-template #maybeJoinOnly>
      <ng-container *ngIf="joinOnly; else nothingSent">Nobody has asked to join yet.</ng-container>
      <ng-template #nothingSent>No messages have been sent yet.</ng-template>
    </ng-template>
  </app-empty-state>

  <ng-container *ngIf="!loading && rows.length > 0">
    <div class="dl-card">
      <table class="dl-table dl-table--cards" dlCardTable>
        <thead>
          <tr>
            <th>Outcome</th>
            <th>To</th>
            <th>Shipment</th>
            <th>Sent</th>
            <th>Opened</th>
            <th>Asked to join</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of rows"
              [class.is-failed]="row.status === 'FAILED'"
              [class.is-lead]="row.joinRequestedAt"
              [class.is-override]="row.joinEmailOverridden">
            <td>
              <span class="sms-outcome" [ngClass]="'sms-outcome--' + outcome(row)">
                {{ outcomeLabel(row) }}
              </span>
            </td>

            <td class="dl-cell--title">
              <div class="sms-primary">{{ row.partnerName || '—' }}</div>
              <div class="sms-secondary">{{ sideLabel(row) }} · {{ row.toPhone || 'no number' }}</div>
            </td>

            <td>
              <div class="sms-primary">
                <ng-container *ngIf="row.orderNumber; else noOrder">#{{ row.orderNumber }}</ng-container>
                <ng-template #noOrder>—</ng-template>
              </div>
              <div class="sms-secondary">
                {{ row.fromPartnerName || '—' }} → {{ row.toPartnerName || '—' }}
              </div>
            </td>

            <td>
              <div class="sms-primary">{{ (row.sentAt || row.createdAt) | date:'dd.MM.yy HH:mm' }}</div>
              <div class="sms-secondary" *ngIf="row.segments">{{ row.segments }} seg</div>
              <!-- The reason matters more than the failure: it names what to go and fix. -->
              <div class="sms-reason" *ngIf="row.errorMessage">{{ row.errorMessage }}</div>
            </td>

            <td>
              <ng-container *ngIf="row.openedAt; else neverOpened">
                <div class="sms-primary">{{ row.openedAt | date:'dd.MM.yy HH:mm' }}</div>
                <!-- More than one visit is a practice using the page, not a glance at a text. -->
                <div class="sms-secondary" *ngIf="row.openCount > 1">{{ row.openCount }} visits</div>
              </ng-container>
              <ng-template #neverOpened><span class="sms-secondary">—</span></ng-template>
            </td>

            <td>
              <ng-container *ngIf="row.joinRequestedAt; else noLead">
                <div class="sms-primary">{{ row.joinRequestedAt | date:'dd.MM.yy HH:mm' }}</div>

                <!-- Both addresses, always together. The one we hold is what makes the one they
                     typed readable: a colleague at the same domain and a stranger look identical
                     on their own. -->
                <ng-container *ngIf="row.joinEmailOverridden; else confirmedAddress">
                  <div class="sms-warning">⚠ Replaced the address</div>
                  <div class="sms-secondary sms-was">was {{ row.partnerEmailOnFile || 'nothing on file' }}</div>
                  <div class="sms-primary">{{ row.joinContact }}</div>
                  <div class="sms-secondary" *ngIf="row.joinClientIp"
                       [title]="row.joinUserAgent || ''">from {{ row.joinClientIp }}</div>
                </ng-container>

                <ng-template #confirmedAddress>
                  <!-- Pressed the button and changed nothing, so the address is the partner's own
                       and lives on their record rather than here. -->
                  <div class="sms-secondary">{{ row.partnerEmailOnFile || 'no address on file' }}</div>
                </ng-template>
              </ng-container>
              <ng-template #noLead><span class="sms-secondary">—</span></ng-template>
            </td>
          </tr>
        </tbody>
      </table>

    <app-pagination
      [currentPage]="page"
      [pageSize]="pageSize"
      [totalElements]="total"
      [totalPages]="totalPages"
      itemLabel="messages"
      (pageChange)="goToPage($event)">
    </app-pagination>
    </div>
  </ng-container>
</div>
